import React, { useEffect, useState } from 'react';
import {
  FaGift, FaAd, FaBookOpen, FaMoneyBillWave, FaPlayCircle,
  FaSmile, FaFacebookSquare,
  FaQuestionCircle, FaCoins, FaClock, FaTrophy, FaRegEye,
  FaGamepad // Added for game module
} from 'react-icons/fa';
import {
  HiOutlineRefresh, HiOutlineCog, HiOutlineQuestionMarkCircle
} from 'react-icons/hi';
import { RiQuestionAnswerLine } from 'react-icons/ri';
import { GiLips } from 'react-icons/gi';
import { toast } from 'react-toastify';
import Calling from '../../api/Calling';
import generateCaptcha from '../../components/captcha';
import Loader from '../../components/Loader';

const SettingCard = ({
  title,
  icon,
  iconBgColor,
  iconColor,
  description,
  children
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 ${iconBgColor} rounded-lg`}>
              {icon}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
              {description && (
                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 bg-gray-50/50">
        {children}
      </div>
    </div>
  );
};

const ToggleSetting = ({
  title,
  description,
  icon,
  iconColor = "text-blue-500",
  isEnabled,
  onToggle,
  isDisabled = false
}) => {
  return (
    <div className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white ${isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          {icon && React.cloneElement(icon, { className: iconColor, size: 18 })}
        </div>
        <div>
          <h3 className="font-medium text-gray-800">{title}</h3>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <label className={`relative inline-flex items-center ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={onToggle}
          className="sr-only peer"
          disabled={isDisabled}
        />
        <div className={`relative w-11 h-6 bg-gray-200 rounded-full peer 
          peer-checked:after:translate-x-full peer-checked:after:border-white 
          after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
          after:bg-white after:border-gray-300 after:border after:rounded-full 
          after:h-5 after:w-5 after:transition-all dark:border-gray-600 
          peer-checked:bg-blue-600`}></div>
      </label>
    </div>
  );
};

const NumberSetting = ({
  title,
  description,
  icon,
  iconColor = "text-blue-500",
  value,
  onChange,
  onUpdate,
  min = 0,
  isDisabled = false
}) => {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-gray-200 rounded-lg bg-white ${isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          {icon && React.cloneElement(icon, { className: iconColor, size: 18 })}
        </div>
        <div>
          <h3 className="font-medium text-gray-800">{title}</h3>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={value}
          min={min}
          onChange={(e) => onChange(Number(e.target.value))}
          className={`w-24 p-2 border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDisabled ? 'bg-gray-100' : 'bg-white'}`}
          disabled={isDisabled}
        />
        <button
          onClick={onUpdate}
          className={`px-4 py-2 bg-blue-500 text-white rounded-lg transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDisabled ? 'cursor-not-allowed opacity-70 bg-gray-400 hover:bg-gray-400' : ''}`}
          disabled={isDisabled}
        >
          Update
        </button>
      </div>
    </div>
  );
};

const InputSetting = ({
  title,
  description,
  icon,
  iconColor = "text-blue-500",
  value,
  onChange,
  placeholder = "",
  isDisabled = false
}) => {
  return (
    <div className={`${isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon && React.cloneElement(icon, { className: iconColor, size: 16 })}
        <label className="block text-sm font-medium text-gray-700">
          {title}
        </label>
      </div>
      {description && <p className="text-xs text-gray-500 mb-2">{description}</p>}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDisabled ? 'bg-gray-100' : 'bg-white'}`}
        placeholder={placeholder}
        disabled={isDisabled}
      />
    </div>
  );
};

const SummaryBox = ({
  title,
  items,
  icon,
  bgColor = "bg-blue-50",
  borderColor = "border-blue-200",
  titleColor = "text-blue-800",
  itemColor = "text-blue-700",
  iconColor = "text-blue-600"
}) => {
  return (
    <div className={`p-5 border ${borderColor} rounded-xl ${bgColor}`}>
      <div className="flex items-start gap-3">
        {icon && React.cloneElement(icon, { className: iconColor, size: 20 })}
        <div>
          <h3 className={`font-medium ${titleColor} mb-2`}>{title}</h3>
          {Array.isArray(items) ? (
            <ul className={`text-xs ${itemColor} space-y-1 list-disc pl-4`}>
              {items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className={`text-xs ${itemColor}`}>{items}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const SelectSetting = ({
  title,
  description,
  icon,
  iconColor = "text-blue-500",
  value,
  onChange,
  options,
  onUpdate,
  isDisabled = false
}) => {
  return (
    <div className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white ${isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          {icon && React.cloneElement(icon, { className: iconColor, size: 18 })}
        </div>
        <div>
          <h3 className="font-medium text-gray-800">{title}</h3>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDisabled ? 'bg-gray-100' : 'bg-white'}`}
          disabled={isDisabled}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <button
          onClick={onUpdate}
          className={`px-4 py-2 bg-blue-500 text-white rounded-lg transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDisabled ? 'cursor-not-allowed opacity-70 bg-gray-400 hover:bg-gray-400' : ''}`}
          disabled={isDisabled}
        >
          Update
        </button>
      </div>
    </div>
  );
};

const Controller = () => {
  const [loader, setLoader] = useState(false);

  // Facebook integration
  const [facebook_app_id, setFacebook_app_id] = useState('');
  const [facebook_client_token, setFacebook_client_token] = useState('');

  // Ad integration
  const [ad_application_id, setAd_application_id] = useState('');
  const [inHouseAds_status, setInHouseAds_status] = useState(false);
  const [NTV_question_status, setNTV_question_status] = useState(false);
  const [NTV_question_counter, setNTV_question_counter] = useState(0);

  // Welcome coins
  const [welcome_coin, setWelcome_coin] = useState(0);
  const [welcome_coin_status, setWelcome_coin_status] = useState(true);

  // Guide settings
  const [guide_quiz, setGuide_quiz] = useState(false);
  const [guide_redeem, setGuide_redeem] = useState(false);
  const [guide_daily, setGuide_daily] = useState(false);
  const [guide_read, setGuide_read] = useState(false);

  // Reward settings
  const [RWD_Daily_END, setRWD_Daily_END] = useState(true);
  const [RWD_Daily_COIN, setRWD_Daily_COIN] = useState(0);
  const [daily_ads_watch_earn_status, setDaily_ads_watch_earn_status] = useState(true);

  // Game settings
  const [play_nonstop_status, setPlay_nonstop_status] = useState(true);
  const [read_and_earn_status, setRead_and_earn_status] = useState(true);
  const [play_tap_win_status, setPlay_tap_win_status] = useState(true);
  const [play_wrong_status, setPlay_wrong_status] = useState(true);
  const [whois_beautiful_status, setWhois_beautiful_status] = useState(true);
  // Added game module status for OGG
  const [game_module_status, setGame_module_status] = useState(false);
  // Added reels status & web view intent
  const [reels_status, setReels_status] = useState(true);
  const [web_view_intend, setWeb_view_intend] = useState(true);

  // Jackpot quiz settings
  const [jackpot_quiz_status, setJackpot_quiz_status] = useState(true);
  const [jackpot_quiz_coin, setJackpot_quiz_coin] = useState(0);
  const [jackpot_quiz_timing, setJackpot_quiz_timing] = useState(5);
  const [jackpot_quiz_watch_ads_status, setJackpot_quiz_watch_ads_status] = useState(true);
  const [jackpot_quiz_watch_ads_coin, setJackpot_quiz_watch_ads_coin] = useState(0);
  const [jackpot_quiz_attempt_limit, setJackpot_quiz_attempt_limit] = useState(1);

  // Withdraw bar settings
  const [winbar_status, setWinbar_status] = useState(true);

  // Ads click & earn settings
  const [ads_click_earn, setAds_click_earn] = useState(false);
  const [ads_click_earn_toast, setAds_click_earn_toast] = useState(true);
  const [ingame_ad_select, setIngame_ad_select] = useState('fs');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getControlleData();
      const { status, message, data } = resp;
      if (status) {
        // Set all data with careful handling of undefined values
        setFacebook_app_id(data?.facebook_app_id || '');
        setFacebook_client_token(data?.facebook_client_token || '');
        setAd_application_id(data?.ad_application_id || '');
        setWelcome_coin(data?.welcome_coin || 0);
        setWelcome_coin_status(data?.welcome_coin_status !== undefined ? data?.welcome_coin_status : true);
        setGuide_quiz(data?.guide_quiz || false);
        setGuide_redeem(data?.guide_redeem || false);
        setGuide_daily(data?.guide_daily || false);
        setGuide_read(data?.guide_read || false);
        setRWD_Daily_END(data?.RWD_Daily_END !== undefined ? data?.RWD_Daily_END : true);
        setRWD_Daily_COIN(data?.RWD_Daily_COIN || 0);
        setNTV_question_counter(data?.NTV_question_counter || 0);
        setNTV_question_status(data?.NTV_question_status || false);
        setDaily_ads_watch_earn_status(data?.daily_ads_watch_earn_status !== undefined ? data?.daily_ads_watch_earn_status : true);
        setRead_and_earn_status(data?.read_and_earn_status !== undefined ? data?.read_and_earn_status : true);
        setPlay_nonstop_status(data?.play_nonstop_status !== undefined ? data?.play_nonstop_status : true);
        setJackpot_quiz_status(data?.jackpot_quiz_status !== undefined ? data?.jackpot_quiz_status : true);
        setJackpot_quiz_coin(Number(data?.jackpot_quiz_coin) || 0);
        setJackpot_quiz_timing(Number(data?.jackpot_quiz_timing) || 5);
        setJackpot_quiz_watch_ads_status(data?.jackpot_quiz_watch_ads_status !== undefined ? data?.jackpot_quiz_watch_ads_status : true);
        setJackpot_quiz_watch_ads_coin(Number(data?.jackpot_quiz_watch_ads_coin) || 0);
        setJackpot_quiz_attempt_limit(Number(data?.jackpot_quiz_attempt_limit) || 1);
        setWinbar_status(data?.winbar_status !== undefined ? data?.winbar_status : true);
        setPlay_tap_win_status(data?.play_tap_win_status !== undefined ? data?.play_tap_win_status : true);
        setPlay_wrong_status(data?.play_wrong_status !== undefined ? data?.play_wrong_status : true);
        setWhois_beautiful_status(data?.whois_beautiful_status !== undefined ? data?.whois_beautiful_status : true);
        setInHouseAds_status(data?.inHouseAds_status !== undefined ? data?.inHouseAds_status : false);
        setAds_click_earn(data?.ads_click_earn !== undefined ? data?.ads_click_earn : false);
        setAds_click_earn_toast(data?.ads_click_earn_toast !== undefined ? data?.ads_click_earn_toast : true);
        setIngame_ad_select(data?.ingame_ad_select || 'fs');
        // Added setting game module status
        setGame_module_status(data?.game_module_status !== undefined ? data?.game_module_status : false);
        // Added setting reels status & web view intent
        setReels_status(data?.reels_status !== undefined ? data?.reels_status : true);
        setWeb_view_intend(data?.web_view_intend !== undefined ? data?.web_view_intend : true);
      } else {
        toast.error(message || "Failed to load settings");
      }
    } catch (error) {
      console.error("Error fetching controller data:", error);
      toast.error('An error occurred while fetching data');
    } finally {
      setLoader(false);
    }
  };

  const handleControllerPosition = async (name, value) => {
    // Validate number inputs
    if ((name === 'welcome_coin' || name === 'RWD_Daily_COIN' || name === 'NTV_question_counter' ||
      name === 'jackpot_quiz_coin' || name === 'jackpot_quiz_watch_ads_coin') && Number(value) < 0) {
      return toast.error(`${name.replace('_', ' ')} must be 0 or more`);
    }

    if (name === 'jackpot_quiz_timing' && Number(value) < 5) {
      return toast.error('Jackpot quiz timing must be at least 5 seconds');
    }

    if (name === 'jackpot_quiz_attempt_limit' && Number(value) < 1) {
      return toast.error('Jackpot quiz attempt limit must be at least 1');
    }

    // Check if user is trying to enable jackpot quiz without required features
    if (name === 'jackpot_quiz_status' && value === true &&
      !daily_ads_watch_earn_status && !read_and_earn_status) {
      return toast.error('Please enable at least one of the required features: Daily Ads Watch & Earn or Read & Earn',
        { autoClose: 4000 });
    }

    const content = `You are about to update ${name}. To confirm, please solve this math problem`;
    const check = generateCaptcha(content);
    if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });

    setLoader(true);
    try {
      const resp = await Calling.updateControllerPosition({ name, status: value });
      if (resp.status) toast.success(resp.message);
      else toast.error(resp.message);
    } catch {
      toast.error('Failed to update controller settings');
    } finally {
      fetchData();
      setLoader(false);
    }
  };

  const handleFacebook = async (e) => {
    e.preventDefault();

    const content = `You are about to update Facebook settings. To confirm, please solve this math problem`;
    const check = generateCaptcha(content);
    if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });

    setLoader(true);
    try {
      const resp = await Calling.setControleFacebook({ facebook_app_id, facebook_client_token });
      if (resp?.status) toast.success(resp?.message);
      else toast.error(resp?.message);
    } catch {
      toast.error('Failed to update Facebook settings');
    } finally {
      fetchData();
      setLoader(false);
    }
  }

  const handleAdApplicationId = async (e) => {
    e.preventDefault();

    // Validate the ad application ID isn't empty
    if (!ad_application_id.trim()) {
      return toast.error('Ad Application ID cannot be empty', { autoClose: 2000 });
    }

    const content = `You are about to update Ad Application ID. To confirm, please solve this math problem`;
    const check = generateCaptcha(content);
    if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });

    setLoader(true);
    try {
      const resp = await Calling.updateControllerPosition({ name: 'ad_application_id', status: ad_application_id });
      if (resp?.status) toast.success(resp?.message);
      else toast.error(resp?.message);
    } catch {
      toast.error('Failed to update Ad Application ID');
    } finally {
      setLoader(false);
    }
  }

  if (loader) {
    return <Loader loader={loader} />;
  }

  return (
    <div className="bg-gray-50 w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Controller Settings</h1>
            <p className="mt-1 text-sm text-gray-500">Manage your application settings and features</p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-white transition-colors duration-200 shadow-sm"
          >
            <HiOutlineRefresh className="text-lg" />
            <span>Refresh</span>
          </button>
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 gap-4">

          <SettingCard
            title="Earning Features"
            description="Configure ways users can earn coins"
            icon={<FaCoins className="text-indigo-600 text-xl" />}
            iconBgColor="bg-indigo-100"
            iconColor="text-indigo-600"
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <ToggleSetting
                  title="Play Nonstop Quiz"
                  description="Main continuous quiz feature"
                  icon={<FaPlayCircle />}
                  iconColor="text-indigo-500"
                  isEnabled={play_nonstop_status}
                  onToggle={() => handleControllerPosition('play_nonstop_status', !play_nonstop_status)}
                />

                <ToggleSetting
                  title="Wrong Answer Quiz"
                  description="Quiz focused on wrong answers"
                  icon={<FaPlayCircle />}
                  iconColor="text-indigo-500"
                  isEnabled={play_wrong_status}
                  onToggle={() => handleControllerPosition('play_wrong_status', !play_wrong_status)}
                />

                <ToggleSetting
                  title="Read & Earn"
                  description="Earn coins by reading articles"
                  icon={<FaBookOpen />}
                  iconColor="text-indigo-600"
                  isEnabled={read_and_earn_status}
                  onToggle={() => handleControllerPosition('read_and_earn_status', !read_and_earn_status)}
                />

                <ToggleSetting
                  title="Tap & Win"
                  description="Earn coins by tapping on rewards"
                  icon={<FaPlayCircle />}
                  iconColor="text-indigo-600"
                  isEnabled={play_tap_win_status}
                  onToggle={() => handleControllerPosition('play_tap_win_status', !play_tap_win_status)}
                />

                <ToggleSetting
                  title="Daily Ads Watch & Earn"
                  description="Watch ads to earn daily coins"
                  icon={<FaRegEye />}
                  iconColor="text-indigo-500"
                  isEnabled={daily_ads_watch_earn_status}
                  onToggle={() => handleControllerPosition('daily_ads_watch_earn_status', !daily_ads_watch_earn_status)}
                />

                <ToggleSetting
                  title="Who Is Beautiful"
                  description="Beauty contest participation"
                  icon={<GiLips />}
                  iconColor="text-indigo-600"
                  isEnabled={whois_beautiful_status}
                  onToggle={() => handleControllerPosition('whois_beautiful_status', !whois_beautiful_status)}
                />

                {/* Added Game Module Status */}
                <ToggleSetting
                  title="Game Module"
                  description="Game module functionality"
                  icon={<FaGamepad />}
                  iconColor="text-indigo-600"
                  isEnabled={game_module_status}
                  onToggle={() => handleControllerPosition('game_module_status', !game_module_status)}
                />

                {/* Added Reels Status */}
                <ToggleSetting
                  title="Reels"
                  description="Reels feature on/off"
                  icon={<FaPlayCircle />}
                  iconColor="text-indigo-600"
                  isEnabled={reels_status}
                  onToggle={() => handleControllerPosition('reels_status', !reels_status)}
                />

                {/* Added Web View Intent */}
                <ToggleSetting
                  title="Web View Intent"
                  description="Open links in web view"
                  icon={<FaRegEye />}
                  iconColor="text-indigo-600"
                  isEnabled={web_view_intend}
                  onToggle={() => handleControllerPosition('web_view_intend', !web_view_intend)}
                />
              </div>
            </div>
          </SettingCard>

          {/* Rewards & Welcome Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Rewards Section */}
            <SettingCard
              title="Daily Quiz Rewards"
              description="Configure daily quiz finish rewards"
              icon={<FaGift className="text-purple-600 text-xl" />}
              iconBgColor="bg-purple-100"
              iconColor="text-purple-600"
            >
              <div className="space-y-4">
                <ToggleSetting
                  title="Daily Quiz Reward Enabled"
                  description="Reward after daily quiz completion"
                  icon={<FaGift />}
                  iconColor="text-purple-500"
                  isEnabled={RWD_Daily_END}
                  onToggle={() => handleControllerPosition('RWD_Daily_END', !RWD_Daily_END)}
                />

                <NumberSetting
                  title="Daily Reward Amount"
                  description="Coins for daily reward"
                  icon={<FaCoins />}
                  iconColor="text-purple-500"
                  value={RWD_Daily_COIN}
                  onChange={setRWD_Daily_COIN}
                  onUpdate={() => handleControllerPosition('RWD_Daily_COIN', RWD_Daily_COIN)}
                  min={0}
                />

              </div>
            </SettingCard>

            {/* Welcome Coins Section */}
            <SettingCard
              title="Welcome Coins"
              description="Initial coins for new users"
              icon={<FaCoins className="text-green-600 text-xl" />}
              iconBgColor="bg-green-100"
              iconColor="text-green-600"
            >
              <div className="space-y-4">
                <ToggleSetting
                  title="Welcome Coins Enabled"
                  description="Give initial coins to new users"
                  icon={<FaCoins />}
                  iconColor="text-green-500"
                  isEnabled={welcome_coin_status}
                  onToggle={() => handleControllerPosition('welcome_coin_status', !welcome_coin_status)}
                />

                <NumberSetting
                  title="Welcome Coin Amount"
                  description="Starting coins for new users"
                  icon={<FaCoins />}
                  iconColor="text-green-500"
                  value={welcome_coin}
                  onChange={setWelcome_coin}
                  onUpdate={() => handleControllerPosition('welcome_coin', welcome_coin)}
                  min={0}
                  isDisabled={!welcome_coin_status}
                />

              </div>
            </SettingCard>
          </div>

          {/* Withdraw & Guide Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Withdraw Section */}
            <SettingCard
              title="Withdrawal Progress Bar"
              description="Configure withdrawal progress visibility"
              icon={<FaMoneyBillWave className="text-emerald-600 text-xl" />}
              iconBgColor="bg-emerald-100"
              iconColor="text-emerald-600"
            >
              <div className="space-y-4">
                <ToggleSetting
                  title="Show Progress Bar"
                  description="Display progress towards withdrawal"
                  icon={<FaMoneyBillWave />}
                  iconColor="text-emerald-500"
                  isEnabled={winbar_status}
                  onToggle={() => handleControllerPosition('winbar_status', !winbar_status)}
                />
              </div>
            </SettingCard>

            {/* Guide Settings */}
            <SettingCard
              title="Guide Videos"
              description="Control instructional guides for users"
              icon={<HiOutlineQuestionMarkCircle className="text-red-600 text-xl" />}
              iconBgColor="bg-red-100"
              iconColor="text-red-600"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ToggleSetting
                    title="Quiz Guide Video"
                    description="Instructions for quiz features"
                    icon={<HiOutlineQuestionMarkCircle />}
                    iconColor="text-red-500"
                    isEnabled={guide_quiz}
                    onToggle={() => handleControllerPosition('guide_quiz', !guide_quiz)}
                  />

                  <ToggleSetting
                    title="Redeem Guide Video"
                    description="Instructions for redeeming rewards"
                    icon={<HiOutlineQuestionMarkCircle />}
                    iconColor="text-red-500"
                    isEnabled={guide_redeem}
                    onToggle={() => handleControllerPosition('guide_redeem', !guide_redeem)}
                  />

                  <ToggleSetting
                    title="Daily Streak Guide"
                    description="Instructions for daily streak feature"
                    icon={<HiOutlineQuestionMarkCircle />}
                    iconColor="text-red-500"
                    isEnabled={guide_daily}
                    onToggle={() => handleControllerPosition('guide_daily', !guide_daily)}
                  />

                  <ToggleSetting
                    title="Read & Earn Guide"
                    description="Instructions for read & earn feature"
                    icon={<HiOutlineQuestionMarkCircle />}
                    iconColor="text-red-500"
                    isEnabled={guide_read}
                    onToggle={() => handleControllerPosition('guide_read', !guide_read)}
                  />
                </div>
              </div>
            </SettingCard>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ads Manager Section */}
            <SettingCard
              title="Ad Management"
              description="Configure all ad-related settings"
              icon={<FaAd className="text-blue-600 text-xl" />}
              iconBgColor="bg-blue-100"
              iconColor="text-blue-600"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <ToggleSetting
                    title="In-House Ads"
                    description="Display in-app promotional content"
                    icon={<FaAd />}
                    iconColor="text-blue-500"
                    isEnabled={inHouseAds_status}
                    onToggle={() => handleControllerPosition('inHouseAds_status', !inHouseAds_status)}
                  />

                  <ToggleSetting
                    title="Native Ad Questions"
                    description="Show questions after native ads"
                    icon={<RiQuestionAnswerLine />}
                    iconColor="text-blue-500"
                    isEnabled={NTV_question_status}
                    onToggle={() => handleControllerPosition('NTV_question_status', !NTV_question_status)}
                  />
                </div>

                <NumberSetting
                  title="Post-Ad Questions"
                  description="Number of questions after native ads"
                  icon={<RiQuestionAnswerLine />}
                  iconColor="text-blue-500"
                  value={NTV_question_counter}
                  onChange={setNTV_question_counter}
                  onUpdate={() => handleControllerPosition('NTV_question_counter', NTV_question_counter)}
                  min={0}
                />

                <SelectSetting
                  title="In-Game Ad Format"
                  description="Format of ads shown during gameplay"
                  icon={<FaAd />}
                  iconColor="text-yellow-600"
                  value={ingame_ad_select}
                  onChange={setIngame_ad_select}
                  options={[
                    { value: 'fs', label: 'Fullscreen' },
                    { value: 'open', label: 'Open' },
                    { value: 'both', label: 'Both' }
                  ]}
                  onUpdate={() => handleControllerPosition('ingame_ad_select', ingame_ad_select)}
                />

                <div className="grid grid-cols-1 gap-4">
                  <ToggleSetting
                    title="Ads Click & Earn"
                    description="Earn coins by clicking on ads"
                    icon={<FaAd />}
                    iconColor="text-yellow-600"
                    isEnabled={ads_click_earn}
                    onToggle={() => handleControllerPosition('ads_click_earn', !ads_click_earn)}
                  />

                  <ToggleSetting
                    title="Ads Click Toast"
                    description="Show notification on ad click earnings"
                    icon={<FaSmile />}
                    iconColor="text-green-600"
                    isEnabled={ads_click_earn_toast}
                    onToggle={() => ads_click_earn && handleControllerPosition('ads_click_earn_toast', !ads_click_earn_toast)}
                    isDisabled={!ads_click_earn}
                  />
                </div>

              </div>
            </SettingCard>

            {/* Jackpot Quiz Section */}
            <SettingCard
              title="Jackpot Quiz Settings"
              description="Configure special timed quiz rewards"
              icon={<FaTrophy className="text-yellow-600 text-xl" />}
              iconBgColor="bg-yellow-100"
              iconColor="text-yellow-600"
            >
              <div className="space-y-4">
                <ToggleSetting
                  title="Enable Jackpot Quiz"
                  description="Special timed quiz with higher rewards"
                  icon={<FaTrophy />}
                  iconColor="text-yellow-500"
                  isEnabled={jackpot_quiz_status}
                  onToggle={() => handleControllerPosition('jackpot_quiz_status', !jackpot_quiz_status)}
                />

                <NumberSetting
                  title="Base Coin Reward"
                  description="Coins awarded for quiz completion"
                  icon={<FaCoins />}
                  iconColor="text-yellow-500"
                  value={jackpot_quiz_coin}
                  onChange={setJackpot_quiz_coin}
                  onUpdate={() => handleControllerPosition('jackpot_quiz_coin', jackpot_quiz_coin)}
                  min={0}
                  isDisabled={!jackpot_quiz_status}
                />

                <NumberSetting
                  title="Time Limit (seconds)"
                  description="How long users have to complete the quiz"
                  icon={<FaClock />}
                  iconColor="text-yellow-500"
                  value={jackpot_quiz_timing}
                  onChange={setJackpot_quiz_timing}
                  onUpdate={() => handleControllerPosition('jackpot_quiz_timing', jackpot_quiz_timing)}
                  min={5}
                  isDisabled={!jackpot_quiz_status}
                />

                <NumberSetting
                  title="Daily Attempt Limit"
                  description="Maximum attempts allowed per day"
                  icon={<FaQuestionCircle />}
                  iconColor="text-yellow-500"
                  value={jackpot_quiz_attempt_limit}
                  onChange={setJackpot_quiz_attempt_limit}
                  onUpdate={() => handleControllerPosition('jackpot_quiz_attempt_limit', jackpot_quiz_attempt_limit)}
                  min={1}
                  isDisabled={!jackpot_quiz_status}
                />

                <ToggleSetting
                  title="Ads Reward Bonus"
                  description="Allow extra coins for watching ads"
                  icon={<FaAd />}
                  iconColor="text-yellow-500"
                  isEnabled={jackpot_quiz_watch_ads_status}
                  onToggle={() => jackpot_quiz_status && handleControllerPosition('jackpot_quiz_watch_ads_status', !jackpot_quiz_watch_ads_status)}
                  isDisabled={!jackpot_quiz_status}
                />

                <NumberSetting
                  title="Ad View Bonus"
                  description="Extra coins for watching post-quiz ad"
                  icon={<FaCoins />}
                  iconColor="text-yellow-500"
                  value={jackpot_quiz_watch_ads_coin}
                  onChange={setJackpot_quiz_watch_ads_coin}
                  onUpdate={() => handleControllerPosition('jackpot_quiz_watch_ads_coin', jackpot_quiz_watch_ads_coin)}
                  min={0}
                  isDisabled={!jackpot_quiz_status || !jackpot_quiz_watch_ads_status}
                />

              </div>
            </SettingCard>
          </div>

          {/* Integration Settings Section */}
          <SettingCard
            title="Integration Settings"
            description="Configure external services integration"
            icon={<HiOutlineCog className="text-gray-600 text-xl" />}
            iconBgColor="bg-gray-100"
            iconColor="text-gray-600"
          >
            <div className="space-y-6">
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Facebook Integration */}
                <div className="p-5 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <FaFacebookSquare className="text-blue-600" />
                    Facebook Integration
                  </h3>

                  <div className="grid grid-cols-1 gap-4 mb-4">
                    <InputSetting
                      title="Facebook App ID"
                      description="Your Facebook application identifier"
                      icon={<FaFacebookSquare />}
                      iconColor="text-blue-600"
                      value={facebook_app_id}
                      onChange={setFacebook_app_id}
                      placeholder="e.g. 135885938132XXXX"
                    />

                    <InputSetting
                      title="Facebook Client Token"
                      description="Authentication token for Facebook API"
                      icon={<FaFacebookSquare />}
                      iconColor="text-blue-600"
                      value={facebook_client_token}
                      onChange={setFacebook_client_token}
                      placeholder="e.g. 964343XX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleFacebook}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                      Save Facebook Settings
                    </button>
                  </div>
                </div>

                {/* Ad Integration */}
                <div className="p-5 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <FaAd className="text-orange-600" />
                    Ad Network Integration
                  </h3>

                  <div className="mb-4">
                    <InputSetting
                      title="Ad Application ID"
                      description="Identifier for your ad network integration"
                      icon={<FaAd />}
                      iconColor="text-orange-600"
                      value={ad_application_id}
                      onChange={setAd_application_id}
                      placeholder="e.g. ca-app-pub-xxxxxxxxxxxxxxxx"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleAdApplicationId}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg transition-colors duration-200 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                      Save Ad Integration
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </SettingCard>

        </div>
      </div>
    </div>
  );
};

export default Controller;